import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { membersAPI, beneficiariesAPI, paymentsAPI, feeConfigAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { StatCard, WelcomeBanner } from '../_shared/DashboardParts';

function formatStatus(status) {
  if (!status) return 'Active';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
}

export default function MemberDashboard() {
  const { user } = useAuthStore();
  const [member, setMember]           = useState(null);
  const [summary, setSummary]         = useState({ nextOfKinCount: 0, beneficiaryCount: 0, pendingPayments: 0, paidPayments: 0 });
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [hasThisYearInvoice, setHasThisYearInvoice] = useState(true);
  const [feeConfig, setFeeConfig]     = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const memberProfile = await membersAPI.me();
        setMember(memberProfile);
        const [benResp, payResp, feeResp] = await Promise.all([
          beneficiariesAPI.getByMember(memberProfile.id),
          paymentsAPI.getByMember(memberProfile.id),
          feeConfigAPI.getAll(),
        ]);
        const nextOfKin = benResp.beneficiaries || [];
        const selectedBens = new Set(
          [benResp.selected_installment_one_beneficiary?.id, benResp.selected_installment_two_beneficiary?.id].filter(Boolean)
        );
        const invoices = payResp.invoices || [];
        const pending  = invoices.filter((p) => String(p.paystatus || '').toUpperCase() !== 'PAID');
        const paid     = invoices.filter((p) => String(p.paystatus || '').toUpperCase() === 'PAID');
        const thisYear = new Date().getFullYear();
        setSummary({ nextOfKinCount: nextOfKin.length, beneficiaryCount: selectedBens.size, pendingPayments: pending.length, paidPayments: paid.length });
        setPendingInvoices(pending);
        setHasThisYearInvoice(invoices.some((p) => Number(p.fee_year) === thisYear));
        setFeeConfig(feeResp);
      } catch {
        // silently ignore — user may not have a member profile yet
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const welcomeName = user?.full_name || user?.name || 'Member';
  const thisYear    = new Date().getFullYear();
  const thisYearFee = (feeConfig?.annual_fees || []).find((f) => Number(f.fee_year) === thisYear);

  return (
    <div className="space-y-5">
      <WelcomeBanner name={welcomeName} subtitle="Welcome to your AWTF member portal" />

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Loading...
        </div>
      )}

      {!loading && (
        <>
          {member?.membership_type === 'life' && (
            <div className="overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-5 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-3xl shadow-md">
                  🏆
                </div>
                <div>
                  <p className="text-base font-black text-amber-900">
                    Congratulations, {welcomeName.split(' ')[0]}! You are an AWTF Life Member.
                  </p>
                  <p className="mt-0.5 text-sm text-amber-700">
                    {member.life_member_date
                      ? `Life member since ${member.life_member_date} — you no longer need to pay AWTF annual contributions.`
                      : 'You no longer need to pay AWTF annual contributions — thank you for supporting the welfare of your fellow advocates.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Stats ── */}
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatCard title="Next of Kin"        value={String(summary.nextOfKinCount)}   icon="members"       color="blue"  />
            <StatCard title="Beneficiaries"       value={String(summary.beneficiaryCount)} icon="beneficiaries" color="green" />
            <StatCard title="Pending Payments"    value={String(summary.pendingPayments)}  icon="alert"         color="amber" />
            <StatCard title="Paid Receipts"       value={String(summary.paidPayments)}     icon="paid"          color="rose"  />
          </div>

          {/* ── Pending invoices alert ── */}
          {pendingInvoices.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500 text-xs font-black text-white">
                  !
                </span>
                <p className="text-sm font-bold text-amber-800">
                  You have {pendingInvoices.length} pending {pendingInvoices.length === 1 ? 'payment' : 'payments'}
                </p>
              </div>
              <div className="space-y-2">
                {pendingInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Annual Fee {inv.fee_year}</p>
                      <p className="font-mono text-xs text-slate-500">{inv.reference_no}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-bold text-amber-700">
                        TZS {Number(inv.fee || 0).toLocaleString()}
                      </p>
                      <Link to="/payments" className="text-xs text-blue-600 hover:underline">
                        Pay now →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-amber-600">
                Pay at any CRDB Bank branch using the reference number above.
              </p>
            </div>
          )}

          {/* ── No invoice yet this year — prompt to generate & pay ── */}
          {!hasThisYearInvoice && !member?.is_senior_exempt && (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-black text-white">
                  !
                </span>
                <p className="text-sm font-bold text-orange-800">
                  Your {thisYear} membership fee has not been paid yet
                </p>
              </div>
              <p className="text-sm text-orange-700">
                {thisYearFee
                  ? `Pay TZS ${Number(thisYearFee.amount).toLocaleString()} to complete your AWTF membership for ${thisYear}.`
                  : `Generate your ${thisYear} invoice to complete your AWTF membership.`}
              </p>
              <Link to="/payments"
                className="mt-3 inline-flex items-center justify-center rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-700">
                Generate &amp; Pay Now →
              </Link>
            </div>
          )}

          {/* ── Quick links ── */}
          <div className="grid gap-4 sm:grid-cols-3">
            <QuickLinkCard
              icon="💳"
              title="Payments"
              description="View and manage your AWTF annual fee invoices."
              to="/payments"
              label="View Payments"
              accent="blue"
            />
            <QuickLinkCard
              icon="🧾"
              title="Receipts"
              description="View and download receipts for all confirmed payments."
              to="/receipts"
              label="View Receipts"
              accent="emerald"
            />
            <QuickLinkCard
              icon="👥"
              title="Beneficiaries"
              description="Manage your next of kin and welfare beneficiaries."
              to="/beneficiaries"
              label="Manage"
              accent="violet"
            />
          </div>

          {/* ── Member profile snapshot ── */}
          {member && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-400">My Profile</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ProfileItem
                  label={member.roll_no ? 'Roll No' : 'Petition No'}
                  value={member.roll_no || member.petition_no || '—'}
                />
                <ProfileItem label="Status"        value={formatStatus(member.status)} highlight />
                <ProfileItem label={`Fee ${thisYear}`}
                  value={thisYearFee ? `TZS ${Number(thisYearFee.amount).toLocaleString()}` : '—'} />
                <ProfileItem label="Pending"
                  value={summary.pendingPayments > 0
                    ? `${summary.pendingPayments} invoice${summary.pendingPayments > 1 ? 's' : ''}`
                    : 'None'} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function QuickLinkCard({ icon, title, description, to, label, accent }) {
  const accents = {
    blue:    { btn: 'bg-blue-600 hover:bg-blue-700 text-white',    ring: 'ring-blue-100'    },
    emerald: { btn: 'bg-emerald-600 hover:bg-emerald-700 text-white', ring: 'ring-emerald-100' },
    violet:  { btn: 'bg-violet-600 hover:bg-violet-700 text-white', ring: 'ring-violet-100'  },
  };
  const a = accents[accent] || accents.blue;
  return (
    <div className={`flex flex-col rounded-2xl bg-white p-5 shadow-sm ring-1 ${a.ring}`}>
      <span className="mb-3 text-2xl">{icon}</span>
      <p className="text-base font-bold text-slate-800">{title}</p>
      <p className="mt-1 flex-1 text-sm text-slate-500">{description}</p>
      <Link to={to}
        className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${a.btn}`}>
        {label} →
      </Link>
    </div>
  );
}

function ProfileItem({ label, value, highlight }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-bold ${highlight ? 'text-emerald-600' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}
